import type {
  Direction,
  Field,
  OrderState,
} from "@v1/components/dashboard/projects/SortDropdown";
import type { ReploSimpleProjectWithStats } from "schemas/generated/project";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import GalleryProjectItem from "@v1/components/dashboard/projects/GalleryProjectItem/index";
import ListProjectItem from "@v1/components/dashboard/projects/ListProjectItem";
import { EmptyProjectMembership } from "@v1/components/dashboard/projects/memberships/EmptyProjectMembership";
import NoMatchingProjects from "@v1/components/dashboard/projects/NoMatchingProjects";
import SortDropdown from "@v1/components/dashboard/projects/SortDropdown";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import AnnouncementBanner from "@v1/features/announcements/AnnouncementBanner";
import { Loader } from "@v2/components/Loader";
import Header from "@v2/features/dashboard/Header";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { getProjectName, getStoreData } from "@v2/utils/project-utils";
import { routes } from "@v2/utils/router";
import { Grid2x2, List, Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { isEmpty } from "replo-utils/lib/misc";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";
import z from "zod";

const viewModeSchema = z.enum(["gallery", "list"]);
type ViewMode = z.infer<typeof viewModeSchema>;

const isViewMode = (value: unknown): value is ViewMode => {
  return viewModeSchema.safeParse(value).success;
};

const LOCAL_STORAGE_VIEW_MODE_KEY = "replo-projects-view-mode";

type ProjectsTableProps = {
  title: string | null;
  subtitle: string;
  projects: ReploSimpleProjectWithStats[] | undefined;
  isLoading: boolean;
  href?: string;
  onButtonClick?: () => void;
  buttonLabel?: string;
  showWorkspaceColumn?: boolean;
};

type ProjectsHeaderProps = {
  title: string | null;
  subtitle: string;
  buttonLabel?: string;
  onButtonClick?: () => void;
  isLoading: boolean;
};

const ProjectsHeader: React.FC<ProjectsHeaderProps> = ({
  title,
  subtitle,
  buttonLabel,
  onButtonClick,
  isLoading,
}) => {
  return (
    <div className="flex flex-col gap-4">
      <Header
        title={title}
        subtitle={subtitle}
        isLoading={isLoading}
        endEnhancer={
          buttonLabel &&
          onButtonClick && (
            <DeprecatedButton
              variant="primary"
              size="base"
              onClick={onButtonClick}
            >
              {buttonLabel}
            </DeprecatedButton>
          )
        }
      />
      <AnnouncementBanner tags={["New"]} />
      <Separator className="col-span-12 mb-4" />
    </div>
  );
};

const ProjectsTable: React.FC<ProjectsTableProps> = ({
  title,
  subtitle,
  projects,
  isLoading,
  onButtonClick,
  buttonLabel = "",
  showWorkspaceColumn = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const logAnalytics = useLogAnalytics();
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(
    LOCAL_STORAGE_VIEW_MODE_KEY,
    "gallery",
    { validate: isViewMode },
  );

  const [order, setOrder] = React.useState<OrderState>({
    field: "project.lastEditedAt",
    direction: "desc",
  });

  const sortedProjects = React.useMemo(
    () => sortProjectMemberships(order.field, order.direction, projects),
    [projects, order.field, order.direction],
  );

  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
  const [searchQueryInputValue, searchQuery, setSearchQuery] =
    useDebouncedState("");

  const filteredProjects = sortedProjects.filter((project) => {
    return getProjectName(project)
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
  });

  const shouldShowEmptyProjectMembership = !isLoading && isEmpty(projects);
  const shouldShowProjectsTable = !isLoading && !isEmpty(projects);

  const onSelectProject = (
    project: ReploSimpleProjectWithStats,
    e?: React.MouseEvent,
  ) => {
    const storeData = getStoreData(project);
    logAnalytics("dashboard.project.click", {
      storeId: project.id,
      name: project.name ?? "",
      storeUrl: storeData?.shopifyUrl ?? undefined,
      from:
        location.pathname === routes.allProjects
          ? "allProjects"
          : "orgProjects",
    } as const);

    // Note (Juan, 2024-12-30) Allow users to open project in new tab with Cmd/Ctrl + click
    // This matches the default browser behavior for links
    if (e?.metaKey || e?.ctrlKey) {
      window.open(`/editor/${project.id}`, "_blank");
      return;
    }

    navigate(`/editor/${project.id}`, {
      state: { getCanvasDoc: true },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      setSelectedIndex((prev) => {
        if (prev === null) {
          return 0;
        } else {
          return (prev + 1) % filteredProjects.length;
        }
      });
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setSelectedIndex((prev) => {
        if (prev === null) {
          return filteredProjects.length - 1;
        } else {
          return (prev - 1 + filteredProjects.length) % filteredProjects.length;
        }
      });
      e.preventDefault();
    } else if (
      e.key === "Enter" &&
      selectedIndex !== null &&
      filteredProjects[selectedIndex]
    ) {
      onSelectProject(filteredProjects[selectedIndex]);
      e.preventDefault();
    } else if (e.key === "Escape") {
      // Extra UX feature to escape the search input
      setSelectedIndex(null);
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
  };

  const renderProjectsList = () => {
    if (filteredProjects.length === 0) {
      return (
        <NoMatchingProjects searchQueryInputValue={searchQueryInputValue} />
      );
    }

    if (viewMode === "gallery") {
      return (
        <div className="grid min-w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredProjects.map((project, index) => (
            <GalleryProjectItem
              key={project.id}
              project={project}
              showWorkspaceColumn={showWorkspaceColumn}
              onSelectProject={(e) => onSelectProject(project, e)}
              className={`${selectedIndex === index ? "bg-sky-soft" : ""}`}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="flex min-w-full flex-col">
        <div className="text-placeholder border-border border-b-0.5 grid w-full grid-cols-12 items-center justify-center gap-2 py-2 pl-4 pr-4 text-sm">
          <div className="col-span-3">Project Name</div>
          {showWorkspaceColumn && <div className="col-span-3">Workspace</div>}
          <div className="col-span-3">Edited</div>
          <div className={showWorkspaceColumn ? "col-span-2" : "col-span-3"}>
            Shopify
          </div>
          <div
            className={showWorkspaceColumn ? "col-span-1" : "col-span-3"}
          ></div>
        </div>
        <div className="divide-y-0.5 divide-border">
          {filteredProjects.map((project, index) => (
            <ListProjectItem
              key={project.id}
              project={project}
              allowsRenaming
              showWorkspaceColumn={showWorkspaceColumn}
              onSelectProject={(e) => onSelectProject(project, e)}
              className={`${selectedIndex === index ? "bg-sky-soft" : ""}`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col px-6">
      <ProjectsHeader
        title={title}
        subtitle={subtitle}
        buttonLabel={buttonLabel}
        onButtonClick={onButtonClick}
        isLoading={isLoading}
      />

      {shouldShowEmptyProjectMembership ? (
        <EmptyProjectMembership
          shouldShowCreateProjectButton={isEmpty(projects)}
        />
      ) : null}
      {isLoading ? (
        <div className="col-span-12">
          <Loader className="mt-16" />
        </div>
      ) : null}
      {shouldShowProjectsTable ? (
        <>
          <div className="mb-4 flex flex-col items-center gap-4 sm:flex-row">
            <div className="w-full sm:max-w-xs sm:flex-1">
              <DeprecatedInput
                size="base"
                placeholder="Search by shop name"
                type="text"
                value={searchQueryInputValue}
                onChange={(event) => setSearchQuery(event.target.value)}
                onEnter={() => {
                  if (filteredProjects.length === 1 && filteredProjects[0]) {
                    onSelectProject(filteredProjects[0]);
                  }
                }}
                onKeyDown={handleKeyDown}
                startEnhancer={<Search className="text-muted" size={16} />}
                onBlur={() => setSelectedIndex(null)}
              />
            </div>

            <div className="w-full sm:w-auto">
              <SortDropdown
                order={order}
                onOrderChange={setOrder}
                showWorkspaceSort={showWorkspaceColumn}
              />
            </div>

            <div className="w-full sm:ml-auto sm:w-auto">
              <DeprecatedToggleGroup
                size="base"
                options={[
                  {
                    label: <Grid2x2 size={16} />,
                    value: "gallery",
                    tooltipContent: "Gallery",
                  },
                  {
                    label: <List size={16} />,
                    value: "list",
                    tooltipContent: "List",
                  },
                ]}
                onChange={(value) => {
                  setViewMode(value as ViewMode);
                  logAnalytics("dashboard.projectView.toggle", {
                    viewMode: value as ViewMode,
                  });
                }}
                selectedValue={viewMode}
              />
            </div>
          </div>
          {renderProjectsList()}
        </>
      ) : null}
    </div>
  );
};

const sortProjectMemberships = (
  field: Field,
  direction: Direction,
  data?: ReploSimpleProjectWithStats[],
) => {
  const sortedData = [...(data ?? [])];
  sortedData.sort((a, b) => {
    if (field === "project.name") {
      const nameA = getProjectName(a).toLowerCase();
      const nameB = getProjectName(b).toLowerCase();
      return direction === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    }
    if (field === "project.lastEditedAt") {
      const dateA: number = new Date(a.lastEditedAt ?? a.createdAt).getTime();
      const dateB: number = new Date(b.lastEditedAt ?? b.createdAt).getTime();
      return direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    if (field === "project.createdAt") {
      const dateA: number = new Date(a.createdAt).getTime();
      const dateB: number = new Date(b.createdAt).getTime();
      return direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    if (field === "project.workspace" && a.ownerWorkspace && b.ownerWorkspace) {
      const nameOfA = a.ownerWorkspace?.name;
      const nameOfB = b.ownerWorkspace?.name;
      return direction === "asc"
        ? nameOfA.localeCompare(nameOfB)
        : nameOfB.localeCompare(nameOfA);
    }
    return 0;
  });
  return sortedData;
};

export default ProjectsTable;
